import {memo, useCallback} from 'react';
import MenuItem, {MenuItemProps} from "./MenuItem";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import MenuAccordion from "./MenuAccordion";
import {ChildrenSidebarProps} from "./Sidebar";
import {useUserContext} from "../../types/contexts/UserContext";
import {useTranslation} from "react-i18next";
import {API} from "../../types/API";
import {useToastContext} from "../../types/contexts/ToastContext";


const AdminSidebar = ({open, handleOpen}: ChildrenSidebarProps) => {

    const {t} = useTranslation();
    const user = useUserContext();
    const toast = useToastContext();


    const updateEncryptionKey = useCallback(() => {
        toast?.show(t('admin.encryption_key.sent'), 'bg-yellow-500');
        API.updateEncryptionKey().then(() => {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.encryption_key.updated')}</>, "bg-teal-500");
        })
    }, [toast]);


    const hasPermission = (key: string) => {
        return user ? user.user.permissions.includes(key) : false;
    }

    const canUpdateEncryptionKey = () => {
        return user ? user.user.roles.includes('Admin') : false;
    }

    const hasAnyOfPermissions = (keys: string[]) => {
        if (!user)
            return false;

        for (let i = 0; i < keys.length; i++) { // @ts-expect-error can be undefined
            if (keys[i] && hasPermission(keys[i]))
                return true;
        }
        return false;
    }

    const reportingItems = () => {
        const result: MenuItemProps[] = [];
        if (hasPermission('view deposit transactions list'))
            result.push({
                name: t('admin.menu.deposits'),
                link: '/app/admin/deposits'
            });

        if (hasPermission('view withdraw transactions list'))
            result.push({
                name: t('admin.menu.withdrawals'),
                link: '/app/admin/withdrawals'
            });

        if (hasPermission('see in/out report'))
            result.push({
                name: t('admin.menu.in_out'),
                link: '/app/admin/in_out_report'
            });

        if (hasPermission('view reporting subscriptions'))
            result.push({
                name: t('admin.menu.subscriptions'),
                link: '/app/admin/subscriptions'
            });

        if (hasPermission('view broker report'))
            result.push({
                name: t('admin.menu.brokers_report'),
                link: '/app/admin/reporting/broker'
            });

        if (hasPermission('view callbacks list'))
            result.push({
                name: t('admin.menu.callbacks_monitoring'),
                link: '/app/admin/reporting/callbacks'
            });

        return result;
    }

    const systemItems = () => {
        const result: MenuItemProps[] = [];
        if (hasPermission('view payout limits list'))
            result.push({name: t('admin.menu.payout_limits'), link: '/app/admin/payout_limits'});

        if (hasPermission('view currencies list'))
            result.push({name: t('admin.menu.currencies'), link: '/app/admin/currencies'});

        if (hasPermission('view countries list'))
            result.push({name: t('admin.menu.countries'), link: '/app/admin/countries'});

        if (hasPermission('view regions list'))
            result.push({name: t('admin.menu.regions'), link: '/app/admin/regions'});

        if (hasPermission('view languages list'))
            result.push({name: t('admin.menu.languages'), link: '/app/admin/languages'});

        if (hasPermission('view translations list'))
            result.push({name: t('admin.menu.translations'), link: '/app/admin/translations'});

        if (hasPermission('view roles list'))
            result.push({name: t('admin.menu.roles'), link: '/app/admin/roles'});

        if (hasPermission('view sources list'))
            result.push({name: t('admin.menu.sources'), link: '/app/admin/sources'});

        if (hasPermission('view logs list'))
            result.push({name: t('admin.menu.logs'), link: '/app/admin/logs'});

        if (canUpdateEncryptionKey())
            result.push({name: t('admin.menu.update_encryption_key'), onClick: updateEncryptionKey});

        return result;
    }

    return (
        <>
            {hasPermission('view users list') &&
                <MenuItem link={'/app/admin/users'} name={t('admin.menu.users')}
                          leftIcon={<FontAwesomeIcon
                              className="text-gray-900 h-5 w-5 dark:text-inherit"
                              icon={solid('users')}/>}/>}
            {hasPermission('view providers list') &&
                <MenuItem link={'/app/admin/providers'} name={t('admin.menu.providers')}
                          leftIcon={<FontAwesomeIcon className="text-gray-900 dark:text-inherit h-5 w-5"
                                                     icon={solid('sack-dollar')}/>}/>}

            {!user?.user.roles.includes('Admin') && hasPermission('view broker payment methods') && user?.user.broker_user_id ?
                <MenuItem link={`/app/admin/users/${user?.user.broker_user_id}/payment_methods`}
                          name={t('admin.menu.broker_payment_methods')}
                          leftIcon={<FontAwesomeIcon className="text-gray-900 dark:text-inherit h-5 w-5"
                                                     icon={solid('money-bill')}/>}/> : ''}

            {hasPermission('view payment methods list') &&
                <MenuItem link={'/app/admin/payment_methods'} name={t('admin.menu.payment_methods')}
                          leftIcon={<FontAwesomeIcon className="text-gray-900 dark:text-inherit h-5 w-5"
                                                     icon={solid('money-bill')}/>}/>}
            {hasPermission('show virtual terminal') &&
                <MenuItem link={'/app/admin/virtual_terminal'}
                          name={t('admin.menu.virtual_terminal')}
                          leftIcon={<FontAwesomeIcon className="text-gray-900 dark:text-inherit h-5 w-5"
                                                     icon={solid('cash-register')}/>}/>}
            {hasPermission('view confirmations list') &&
                <MenuItem link={'/app/admin/confirmations'} name={t('admin.menu.confirmations')}
                          leftIcon={<FontAwesomeIcon className="text-gray-900 dark:text-inherit h-5 w-5"
                                                     icon={solid('check-double')}/>}/>}
            {hasAnyOfPermissions(['view deposit transactions list', 'view withdraw transactions list', 'see in/out report', 'view reporting subscriptions']) &&
                <MenuAccordion name={t('admin.menu.reporting')}
                               isActive={open === 1}
                               index={1}
                               setActive={handleOpen}
                               list={reportingItems()}
                               icon={<FontAwesomeIcon
                                   className="text-gray-900 dark:text-gray-500 h-5 w-5"
                                   icon={solid("file-invoice")}/>}/>}
            <hr className="my-2 border-blue-gray-50 dark:border-blue-gray-800"/>

            {hasAnyOfPermissions(['view payout limits list', 'view currencies list', 'view countries list', 'view languages list', 'view roles list', 'view translations list', 'view logs list', 'view regions list', 'view sources list'])
                && <MenuAccordion name={t('admin.menu.system')}
                                  isActive={open === 3}
                                  index={3}
                                  setActive={handleOpen}
                                  list={systemItems()}
                                  icon={<FontAwesomeIcon className="text-gray-900 dark:text-gray-500 h-5 w-5"
                                                         icon={solid("wrench")}/>}/>}
        </>
    );
}

export default memo(AdminSidebar);
